import { useState, useMemo, useEffect } from 'react'
import config from '@/config'
import * as actions from '@actions'
import usePayments from '@hook/usePayments'
import { useWire, useWireState, useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import {
    BATCH_UPLOAD_STATE_PENDING,
    ORG_DOCUMENT_STATUS_MODEL_APPLIED,
    PURCHASE_TYPE_PROCESS_DOCUMENT,
} from '@constants'
import { nanoid } from 'nanoid'

const fileSizeLimit = config.maxFileUploadMb * 1024 * 1024 // bytes

const UPLOAD_STEPS = {
    PRE_UPLOAD: 'pre_upload',
    UPLOADING: 'uploading',
}

/**
 * Upload document dialog view model
 * 
 * @param {boolean} open
 * @param {Function} setOpen
 * @param {UUID} collectionId
 * @param {UUID} runsheetId
 * @param {UUID} runsheetTabId
 * @param {Array} files
 * @param {Function} setUpdateDocs
 * @returns {UploadDocumentDialogViewModelResult}
 * @constructor
 */
const UploadDocumentDialogViewModel = ({
    open,
    setOpen,
    collectionId,
    runsheetId,
    runsheetTabId,
    files,
    setUpdateDocs,
}) => {
    
    const [uploadData, setUploadData] = useState([])
    const [uploadStep, setUploadStep] = useState(open ? UPLOAD_STEPS.PRE_UPLOAD : null)
    const [shouldProcessDocs, setShouldProcessDocs] = useState(false)
    const [showPaymentInnerModal, setShowPaymentInnerModal] = useState(false)
    const [description, setDescription] = useState('')
    const [validUploadData, setValidUploadData] = useState([])
    const [duplicateFiles, setDuplicateFiles] = useState([])
    const [hasInsufficientBalance, setHasInsufficientBalance] = useState(false)
    
    const remoteConfig = useWireValue(store.remoteConfig)
    const [skipUploadHelperInfoDialog, setSkipUploadHelperInfoDialog] = useWireState(store.skipUploadHelperInfoDialog)
    
    /** @type {Wire<StagedUploadOptions>} */
    const stagedUploadOptions = useWire(store.stagedUploadOptions)
    /** @type {Wire<StagedUploadItem[]>} */
    const stagedUploadFiles = useWire(store.stagedUploadFiles)
    
    const payments = usePayments()
    
    const isSingle = useMemo(() => files.length < 2, [files])
    
    const toggleUploadDocumentDialogOpen = () => setOpen(prev => !prev)
    
    const batchUploadDocuments = () => {
        
        const files = [...validUploadData]
        
        console.log('batchUploadDocuments', files)
        setUploadStep(UPLOAD_STEPS.UPLOADING)
        
        /** @type {StagedUploadOptions} */
        const nextStagedUploadOptions = {
            collectionId,
            runsheetId,
            runsheetTabId,
            fundingSource: payments.fundingSource,
        }
        
        /** @type {StagedUploadItem[]} */
        const nextStagedUploadFiles = files.map(it => ({
            ...it,
            id: nanoid(),
            max: it.file.size,
            value: 0,
            state: BATCH_UPLOAD_STATE_PENDING,
            description,
            shouldAutoMLProcessDocument: it.orgStatus === ORG_DOCUMENT_STATUS_MODEL_APPLIED
                ? false : shouldProcessDocs,
        }))
        
        stagedUploadOptions.setValue(nextStagedUploadOptions)
        stagedUploadFiles.setValue(nextStagedUploadFiles)
        
        setUpdateDocs(true)
        
        if (skipUploadHelperInfoDialog)
            setOpen(false)
        
    }
    
    const fileValidations = {
        isFileSizeValid: fileSize => fileSize < fileSizeLimit,
        possiblyChecked: sha1 => validUploadData.some(data => data.sha1 === sha1),
        isChecked: fileName => validUploadData.some(data => data.file.name === fileName),
        hasSimilarChecked: (sha1, fileName) =>
            validUploadData.some(data => data.sha1 === sha1 && data.file.name !== fileName),
        isDuplicate: (sha1, duplicates) =>
            (duplicates ?? duplicateFiles).some(group => group.some(item => item.sha1 === sha1)),
        existsWithinAnotherCollection: data => data.existsWithinAnotherCollection,
        existsWithinCurrentCollection: data => data.existsWithinCurrentCollection,
        existsWithinAnotherCollectionPreprocess: data => data.existsWithinAnotherCollectionPreprocess,
    }
    
    const fileIsWarning = data => (
        (
            fileValidations.existsWithinAnotherCollection(data) &&
            !fileValidations.existsWithinCurrentCollection(data) &&
            !fileValidations.existsWithinAnotherCollectionPreprocess(data)
        ) ||
        (
            fileValidations.existsWithinAnotherCollection(data) &&
            fileValidations.existsWithinAnotherCollectionPreprocess(data)
        ) ||
        (
            fileValidations.isDuplicate(data.sha1) &&
            !fileValidations.possiblyChecked(data.sha1)
        ) &&
        !fileIsError(data)
    )
    
    const fileIsError = data => !fileValidations.isFileSizeValid(data.file.size) ||
        fileValidations.existsWithinCurrentCollection(data) ||
        fileValidations.hasSimilarChecked(data.sha1, data.file.name)
    
    const handleWithTooltipMessage = data => {
        
        if (!fileValidations.isFileSizeValid(data.file.size))
            return 'This file has more than 100mb.'
        
        if (fileValidations.existsWithinCurrentCollection(data))
            return 'This file has already been uploaded to this collection.'
        
        if (fileValidations.isDuplicate(data.sha1) && !fileValidations.isChecked(data.file.name))
            return 'This file has some duplicates within the current upload. Just one will be uploaded.'
        
        if (fileValidations.existsWithinAnotherCollection(data) 
            && !fileValidations.existsWithinAnotherCollectionPreprocess)
            return 'This document has already been uploaded in another collection and will be copied with any values '
                + 'that have already been extracted.'
        
        if (fileValidations.existsWithinAnotherCollectionPreprocess(data))
            return 'This document has already been uploaded in another collection but still preprocessing. It will be '
                + 'uploaded again.'
        
    }
    
    const handleWithValidUploadData = (data, checked) => setValidUploadData(prev => checked
        ? [...prev, data]
        : prev.filter(it => it.file.name !== data.file.name))
    
    const verifyIfExistsBySha1 = async files => {
        
        const getUniqueSha1 = (acc, cur) => acc.includes(cur.sha1)
            ? acc
            : [...acc, cur.sha1]
        
        const res = await actions.verifyIfExistsBySha1(files.reduce(getUniqueSha1, []), collectionId)
        let verifiedFiles = []
        
        res.forEach(r => {
            
            const filteredFiles = files.filter(e => e.sha1 === r.sha1)
            const filesWithExists = filteredFiles.map(file => ({
                ...file,
                ...r,
            }))
            
            if (!verifiedFiles.some(file => file.sha1 === r.sha1))
                verifiedFiles = verifiedFiles.concat(filesWithExists)
            
        })
        
        return verifiedFiles.concat(files.filter(file => !file.sha1))
        
    }
    
    const getFileSha1 = async file => new Promise((resolve, reject) => {
        
        let reader = new FileReader()
        
        reader.onload = async e => {
            
            const arrayBuffer = e.target.result
            const hashBuffer = await crypto.subtle.digest('SHA-1', arrayBuffer)
            const hashArray = Array.from(new Uint8Array(hashBuffer))
            const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
            
            resolve({ sha1: hashHex, file })
            
        }
        
        reader.onerror = error => reject(error)
        
        reader.readAsArrayBuffer(file)
        
    })
    
    const verifyFiles = async () => {
        
        const filesWithSha1 = []
        
        for (const file of files) {
            const fileWithSha1 = file.size < fileSizeLimit ? await getFileSha1(file) : { file }
            
            filesWithSha1.push(fileWithSha1)
        }
        
        const verifiedFiles = await verifyIfExistsBySha1(filesWithSha1)
        
        setUploadData(verifiedFiles)
        
    }
    
    const findAndGroupDuplicates = array => {
        
        const groupedDuplicates = []
        const seen = new Map()
        
        array.forEach(obj => {
            const { sha1 } = obj
            
            if (seen.has(sha1)) {
                const index = seen.get(sha1)
                
                groupedDuplicates[index].push(obj)
            } else {
                seen.set(sha1, groupedDuplicates.length)
                groupedDuplicates.push([obj])
            }
        })
        
        const unique = array.filter(obj => {
            const index = seen.get(obj.sha1)
            
            return groupedDuplicates[index].length === 1
        })
        
        unique.forEach(obj => {
            const index = seen.get(obj.sha1)
            
            groupedDuplicates[index] = []
        })
        
        const filteredDuplicates = groupedDuplicates.filter(group => group.length > 0)
        
        return { unique, duplicates: filteredDuplicates }
        
    }
    
    useEffect(() => {
        if (shouldProcessDocs) {
            setShowPaymentInnerModal(payments.shouldShowPurchaseDialog(
                PURCHASE_TYPE_PROCESS_DOCUMENT,
                validUploadData.length,
            ))
        } else {
            setHasInsufficientBalance(false)
        }
        
    }, [shouldProcessDocs, validUploadData])
    
    useEffect(() => {
        const { duplicates } = findAndGroupDuplicates(uploadData)
        
        setDuplicateFiles(duplicates)
        
        setValidUploadData(
            uploadData.filter(data =>
                fileValidations.isFileSizeValid(data.file.size) &&
                !fileValidations.existsWithinCurrentCollection(data) &&
                !fileValidations.existsWithinAnotherCollection(data) &&
                !fileValidations.isDuplicate(data.sha1, duplicates),
            ),
        )
    }, [uploadData])
    
    const enabledFiles = useMemo(() => uploadData.filter(it => !fileIsError(it)), [uploadData])
    
    useEffect(() => {
        verifyFiles()
    }, [files])
    
    const modalTitle = useMemo(() => (isSingle ? 'Upload File' : 'Upload Multiple Files'), [isSingle])
    const uploadingTitle = useMemo(() => (isSingle ? 'Uploading File' : 'Uploading Multiple Files'), [isSingle])
    
    return {
        
        // State
        uploadData,
        setUploadData,
        uploadStep,
        setUploadStep,
        shouldProcessDocs,
        setShouldProcessDocs,
        showPaymentInnerModal,
        setShowPaymentInnerModal,
        description,
        setDescription,
        validUploadData,
        setValidUploadData,
        duplicateFiles,
        setDuplicateFiles,
        hasInsufficientBalance,
        setHasInsufficientBalance,
        
        // Global State
        remoteConfig,
        skipUploadHelperInfoDialog,
        setSkipUploadHelperInfoDialog,
        stagedUploadOptions,
        stagedUploadFiles,
        
        // Hooks
        payments,
        
        // Memos
        isSingle,
        enabledFiles,
        modalTitle,
        uploadingTitle,
        
        // Methods
        toggleUploadDocumentDialogOpen,
        batchUploadDocuments,
        fileValidations,
        fileIsWarning,
        fileIsError,
        handleWithTooltipMessage,
        handleWithValidUploadData,
        verifyFiles,
        findAndGroupDuplicates,
        
    }
    
}

export default UploadDocumentDialogViewModel
